import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { fetchCycles } from '../services/reviewCycleService';
import { fetchFormsForCycle, approveReviewForm, signOffReviewForm, transitionStatus } from '../services/reviewFormService';
import WorkflowStatusBadge from '../components/WorkflowStatusBadge';
import ScoreBar from '../components/ScoreBar';
import { WORKFLOW_STEPS } from '../types';
import type { ReviewForm, ReviewCycle, ReviewWorkflowStatus } from '../types';

const APPROVAL_ROLES = ['admin', 'hr_admin', 'leader'];

const ApprovalPanel: React.FC = () => {
  const { user } = useAuth();
  const [cycles, setCycles] = useState<ReviewCycle[]>([]);
  const [selectedCycleId, setSelectedCycleId] = useState('');
  const [forms, setForms] = useState<ReviewForm[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<ReviewWorkflowStatus | 'all'>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchCycles().then(c => {
      setCycles(c);
      const active = c.find(x => x.status === 'active');
      if (active) setSelectedCycleId(active.id);
    });
  }, []);

  useEffect(() => {
    if (!selectedCycleId) return;
    loadForms(selectedCycleId);
  }, [selectedCycleId]);

  const loadForms = async (cid: string) => {
    setLoading(true);
    try {
      const f = await fetchFormsForCycle(cid);
      setForms(f);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };

  const handleBulkApprove = async () => {
    if (!user || selected.size === 0) return;
    if (!confirm(`Approve ${selected.size} review form${selected.size > 1 ? 's' : ''}?`)) return;
    setBusy(true);
    setError('');
    try {
      await Promise.all([...selected].map(id => approveReviewForm(id, user.id)));
      setSelected(new Set());
      await loadForms(selectedCycleId);
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  };

  const handleBulkSignOff = async () => {
    if (!user || selected.size === 0) return;
    if (!confirm(`Sign off ${selected.size} review form${selected.size > 1 ? 's' : ''}?`)) return;
    setBusy(true);
    setError('');
    try {
      await Promise.all([...selected].map(id => signOffReviewForm(id, user.id)));
      setSelected(new Set());
      await loadForms(selectedCycleId);
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  };

  const handleSendToCalibration = async () => {
    if (!user || selected.size === 0) return;
    setBusy(true);
    setError('');
    try {
      await Promise.all([...selected].map(id => transitionStatus(id, 'calibration', user.id)));
      setSelected(new Set());
      await loadForms(selectedCycleId);
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  };

  const filtered = forms.filter(f => filter === 'all' || f.status === filter);

  const statusCounts = forms.reduce<Record<string, number>>((acc, f) => {
    acc[f.status] = (acc[f.status] ?? 0) + 1;
    return acc;
  }, {});

  const canApprove = APPROVAL_ROLES.includes(user?.role ?? '');

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Approval Panel</h1>

      {/* Cycle selector */}
      <select
        value={selectedCycleId}
        onChange={e => setSelectedCycleId(e.target.value)}
        className="bg-white text-gray-900 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
      >
        <option value="">— select cycle —</option>
        {cycles.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>

      {loading && <p className="text-gray-500 animate-pulse">Loading…</p>}

      {!loading && selectedCycleId && (
        <>
          {/* Status overview */}
          <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
            {WORKFLOW_STEPS.slice(-5).map(s => (
              <button
                key={s}
                onClick={() => setFilter(filter === s ? 'all' : s)}
                className={`p-2 rounded-lg border text-xs text-center transition ${
                  filter === s ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-500 hover:border-gray-400'
                }`}
              >
                <div className="font-bold text-lg text-gray-900">{statusCounts[s] ?? 0}</div>
                <div>{s.replace('_', ' ')}</div>
              </button>
            ))}
          </div>

          {/* Bulk actions */}
          {selected.size > 0 && canApprove && (
            <div className="flex items-center gap-3 p-3 bg-brand-accent/10 border border-brand-accent/30 rounded-xl flex-wrap">
              <span className="text-sm text-brand-accent font-medium">{selected.size} selected</span>
              <button onClick={handleSendToCalibration} disabled={busy} className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded-lg transition">
                → Calibration
              </button>
              <button onClick={handleBulkApprove} disabled={busy} className="text-xs bg-brand-success/20 border border-brand-success/40 text-brand-success px-3 py-1.5 rounded-lg transition hover:bg-brand-success/30">
                ✅ Approve Selected
              </button>
              <button onClick={handleBulkSignOff} disabled={busy} className="text-xs bg-emerald-700/30 border border-emerald-500/40 text-emerald-300 px-3 py-1.5 rounded-lg transition hover:bg-emerald-700/50">
                🏁 Sign Off Selected
              </button>
              <button onClick={() => setSelected(new Set())} className="text-xs text-gray-500 hover:text-gray-900 ml-auto">Clear</button>
            </div>
          )}

          {error && <p className="text-brand-error text-sm">{error}</p>}

          {/* Forms list */}
          <div className="space-y-2">
            {filtered.map(f => {
              const stepIdx = WORKFLOW_STEPS.indexOf(f.status);
              const pct = Math.round((stepIdx / (WORKFLOW_STEPS.length - 1)) * 100);
              return (
                <div
                  key={f.id}
                  className={`bg-white border rounded-xl p-4 transition ${
                    selected.has(f.id) ? 'border-blue-400' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {canApprove && (
                      <input
                        type="checkbox"
                        checked={selected.has(f.id)}
                        onChange={() => toggleSelect(f.id)}
                        className="mt-1 accent-brand-accent"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                        <div>
                          <p className="font-semibold text-gray-900 text-sm">{f.revieweeName}</p>
                          <p className="text-xs text-gray-500">Reviewer: {f.reviewerName || f.reviewerId}</p>
                        </div>
                        <WorkflowStatusBadge status={f.status} size="sm" />
                      </div>

                      {/* Workflow progress mini bar */}
                      <div className="w-full bg-gray-200 rounded-full h-1 mb-2">
                        <div className="h-1 rounded-full bg-brand-accent transition-all" style={{ width: `${pct}%` }} />
                      </div>

                      {/* Score summary */}
                      <div className="flex items-center gap-4 flex-wrap text-xs">
                        {f.computedScore != null && (
                          <span className="text-gray-500">Computed: <strong className="text-gray-900">{f.computedScore.toFixed(2)}</strong></span>
                        )}
                        {f.calibratedScore != null && (
                          <span className="text-gray-500">Calibrated: <strong className="text-gray-900">{f.calibratedScore.toFixed(2)}</strong></span>
                        )}
                        {f.discretionAdjustment != null && (
                          <span className="text-gray-500">Adj: <strong className={f.discretionAdjustment >= 0 ? 'text-brand-success' : 'text-brand-error'}>
                            {f.discretionAdjustment >= 0 ? '+' : ''}{f.discretionAdjustment.toFixed(2)}
                          </strong></span>
                        )}
                        {f.finalScore != null && (
                          <span className="text-gray-500">Final: <strong className="text-brand-success text-sm">{f.finalScore.toFixed(2)}</strong></span>
                        )}
                      </div>

                      {f.finalScore != null && <ScoreBar score={f.finalScore} showValue={false} size="sm" />}

                      {/* Individual actions */}
                      <div className="flex gap-2 mt-2 flex-wrap">
                        {f.status === 'calibration' && canApprove && (
                          <button
                            onClick={async () => { setBusy(true); await approveReviewForm(f.id, user!.id); await loadForms(selectedCycleId); setBusy(false); }}
                            className="text-xs bg-brand-success/20 border border-brand-success/40 text-brand-success px-2 py-1 rounded transition hover:bg-brand-success/30"
                          >
                            Approve
                          </button>
                        )}
                        {f.status === 'approved' && canApprove && (
                          <button
                            onClick={async () => { setBusy(true); await signOffReviewForm(f.id, user!.id); await loadForms(selectedCycleId); setBusy(false); }}
                            className="text-xs bg-emerald-700/30 border border-emerald-500/40 text-emerald-300 px-2 py-1 rounded transition"
                          >
                            Sign Off
                          </button>
                        )}
                        {f.approvedAt && (
                          <span className="text-xs text-gray-400">Approved ✓</span>
                        )}
                        {f.signedOffAt && (
                          <span className="text-xs text-emerald-400">Signed off 🏁</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            {filtered.length === 0 && (
              <p className="text-gray-400 text-sm text-center py-10">
                No forms with status "{filter === 'all' ? 'any' : filter}".
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ApprovalPanel;
